import React from "react";
import { UserRoundIcon } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/ui/PageHero";
import { CtaBand } from "../components/ui/CtaBand";
import { Reveal } from "../components/ui/Reveal";
import { PlaceholderTag } from "../components/ui/PlaceholderTag";
import { SectionHeading } from "../components/ui/SectionHeading";
const BUILDERS = [{
  name: "Builder name — placeholder",
  role: "Senior designer & developer"
}, {
  name: "Builder name — placeholder",
  role: "Senior designer & developer"
}];
const VALUES = [{
  title: "No account managers",
  text: "You work directly with the people building your site. Questions get answered by someone who knows the code."
}, {
  title: "Fixed price",
  text: "Website Development is $600 one time. Hosting & Maintenance is $75/month. Scope is agreed in writing first."
}, {
  title: "No lock-in",
  text: "Hosting is month-to-month. If we're not earning your business, you can cancel anytime."
}, {
  title: "Plain language",
  text: "No jargon. We explain what we're doing and why."
}];
export function About() {
  useSeo({
    title: "About",
    description: "NEXORA Web Studio is a two-person senior studio building modern websites with fixed pricing and no lock-in.",
    path: "/about"
  });
  return <>
      <PageHero crumbs={[{
      label: "About"
    }]} eyebrow="About" title="Two senior builders. One standard." lead="NEXORA Web Studio designs, builds and hosts websites for businesses that deserve better than a template." data-id="element-506" data-mp="1yilshvii2648" />

      <section className="shell section grid gap-[clamp(2.5rem,5vw,7rem)] lg:grid-cols-[0.8fr_1.2fr]" data-id="element-507" data-mp="2m8pw42vou5v8">
        <Reveal data-id="element-508" data-mp="2i7mhtktaghw4">
          <p className="t-eyebrow text-accent-soft" data-id="element-509" data-mp="2i74czzsk8vcl">Our story</p>
          <div className="mt-5" data-id="element-510" data-mp="2w75bx9p9lspf"><PlaceholderTag data-id="element-511" data-mp="2w6m4imx8qmpn">Studio story — to be supplied</PlaceholderTag></div>
        </Reveal>
        <Reveal delay={0.05} className="grid gap-6" data-id="element-512" data-mp="2r8avufiomi90">
          <p className="t-lead text-fg-2" data-id="element-513" data-mp="2r9vr67ctm57x">
            Most small businesses are stuck between DIY builders that look generic and agencies with layers of account managers. NEXORA sits in between: senior builders, fixed pricing, and no lock-in.
          </p>
          <p className="t-body max-w-[64ch] text-muted" data-id="element-514" data-mp="1xv2s10vhinpe">
            Placeholder — add the founders' own story here: how the studio began, the kinds of businesses you love working with, and what you want every client to feel when their new site goes live.
          </p>
        </Reveal>
      </section>

      <section className="shell pb-[clamp(4.5rem,8vw,11rem)]" data-id="element-515" data-mp="2cjfpk2a80em0">
        <SectionHeading eyebrow="The builders" title="Who you'll work with." data-id="element-516" data-mp="2cm1v5wdjwk1l" />
        <ul className="grid gap-[clamp(1rem,1.6vw,2rem)] md:grid-cols-2" data-id="element-517" data-mp="2gmlwzd92kbu2">
          {BUILDERS.map((b, i) => <Reveal as="li" key={i} delay={i * 0.05} className="overflow-hidden rounded-[clamp(1rem,1.2vw,1.5rem)] border border-line bg-ink-2" data-id="element-518" data-mp="2lijc5g8ljc59" data-mpi={i}>
              <div className="placeholder-hatch grid aspect-[4/3] place-items-center bg-ink-3 text-muted" data-id="element-519" data-mp="r7xi95d0ahsv">
                <span className="flex flex-col items-center gap-2" data-id="element-520" data-mp="joepw8hvhc7m"><UserRoundIcon className="h-10 w-10" aria-hidden data-id="element-521" data-mp="ewn0i1agdcj2" />Photo placeholder</span>
              </div>
              <div className="flex items-end justify-between gap-4 p-[clamp(1.25rem,2vw,2.25rem)]" data-id="element-522" data-mp="9xaf7kz8cdil">
                <div data-id="element-523" data-mp="4yqq5bpcalm0">
                  <h3 className="t-h3" data-id="element-524" data-mp="4z8m0783axte">{b.name}</h3>
                  <p className="mt-1 text-muted" data-id="element-525" data-mp="4wmda49gqc9l">{b.role}</p>
                </div>
                <PlaceholderTag data-id="element-526" data-mp="12lfzy7qaa2if" />
              </div>
            </Reveal>)}
        </ul>
      </section>

      <section className="border-y border-line bg-ink-2" data-id="element-527" data-mp="xl1oefph4afn">
        <div className="shell section" data-id="element-528" data-mp="sn0a082zszai">
          <SectionHeading eyebrow="Values" title="No account managers. Fixed price. No lock-in." data-id="element-529" data-mp="sq4gjc3xk74c" />
          <ul className="grid border-t border-line-strong md:grid-cols-2" data-id="element-530" data-mp="ooj2vojknot3">
            {VALUES.map((v, i) => <Reveal as="li" key={v.title} delay={i * 0.04} className="border-b border-line py-[clamp(1.5rem,2.5vw,3rem)] md:odd:pr-12 md:even:border-l md:even:pl-12" data-id="element-531" data-mp="39coxjzvi0feq" data-mpi={i}>
                <h3 className="t-h3" data-id="element-532" data-mp="112o5jdcc70qi">{v.title}</h3>
                <p className="t-body mt-3 max-w-[52ch] text-muted" data-id="element-533" data-mp="15v7jibexrxvu">{v.text}</p>
              </Reveal>)}
          </ul>
        </div>
      </section>

      <CtaBand source="about" data-id="element-534" data-mp="r3q97o8o62wy" />
    </>;
}